/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import PercentageSeriesTooltip from "components/charts/modern/tooltip/PercentageSeriesTooltip";
import getContourColors from "components/stocks/modern/OperatingSegments/getContourColors";
import classNames from "components/ui/classNames";
import { useMinWidthTailwindBreakpoints } from "components/ui/hooks/useTailwindBreakpoints";
import aeonik from "components/utils/aeonik";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage } from "react-intl";
import colors from "tailwindcss/colors";
import tinycolor from "tinycolor2";

interface SegmentsPieChartLegend {
  nodes: string[];
  hoverNode: string | null;
  selectedNode: string | null;
}
interface SegmentsPieChartProps {
  className?: string;
  segments: {
    name: string;
    value: number;
  }[];
}
export default function SegmentsPieChart({ className, segments }: SegmentsPieChartProps) {
  const [legend, setLegend] = useState<SegmentsPieChartLegend>({
    nodes: segments.sort((a, b) => (a.value > b.value ? 1 : -1)).map((node) => node.name),
    hoverNode: null,
    selectedNode: null,
  });

  useEffect(
    () =>
      setLegend({
        nodes: segments.sort((a, b) => (a.value > b.value ? 1 : -1)).map((node) => node.name),
        hoverNode: null,
        selectedNode: null,
      }),
    [segments],
  );

  const breakpoints = useMinWidthTailwindBreakpoints();
  const enableCustomLegend = segments.length > 6;

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      type: "pie",
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 0,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    legend: {
      align: "center",
      enabled: true,
      padding: 4,
      alignColumns: true,
      itemDistance: 16,
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: colors.slate[800],
        fontFamily: aeonik.style.fontFamily,
        fontWeight: "500",
        fontSize: "12px",
        textOverflow: "ellipsis",
        width: 240,
        textOutline: "none",
      },
    },
    series: [
      {
        type: "pie",
        size: "70%",
        innerSize: "50%",
        borderRadius: 4,
        showInLegend: !enableCustomLegend,
        data: segments
          .filter((node) => {
            if (enableCustomLegend) {
              return legend.selectedNode ? legend.selectedNode === node.name : true;
            }

            return true;
          })
          .map((node, index) => {
            const theme = getContourColors(index);

            const hasHover = legend.hoverNode !== null;
            const isHoverNode = legend.hoverNode === node.name;

            const color =
              enableCustomLegend && hasHover && !isHoverNode
                ? tinycolor(theme.background).setAlpha(0.2).toRgbString()
                : theme.background;
            const borderColor =
              enableCustomLegend && hasHover && !isHoverNode
                ? tinycolor(theme.text).setAlpha(0.2).toRgbString()
                : theme.text;

            return {
              name: node.name,
              y: node.value,
              color,
              borderColor,
              borderWidth: 1.5,
              custom: node,
              dataLabels: {
                enabled: breakpoints.lg || breakpoints.xl,
                style: {
                  color: enableCustomLegend && hasHover && !isHoverNode ? colors.slate[200] : colors.slate[800],
                  fontFamily: aeonik.style.fontFamily,
                  fontWeight: "500",
                  fontSize: "12px",
                  textOutline: "none",
                  textOverflow: "ellipsis",
                  width: 240,
                },
              },
            };
          }),
      },
    ],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return renderToStaticMarkup(<PercentageSeriesTooltip context={this} />);
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            callback: () => true,
          },
          chartOptions: {
            chart: {
              margin: [-64, 32, 32, 32],
            },
          },
        },
        {
          condition: {
            callback: () => {
              return breakpoints.md;
            },
          },
          chartOptions: {
            chart: {
              margin: [0],
            },
          },
        },
        {
          condition: {
            callback: () => {
              return breakpoints.lg || breakpoints.xl;
            },
          },
          chartOptions: {
            chart: {
              margin: 64,
            },
          },
        },
      ],
    },
  };
  return (
    <div className="relative z-0 flex h-full min-h-[504px] w-full flex-col">
      <HighchartsChart options={options} className={className} />

      <SegmentPieChartLegend isEnable={enableCustomLegend} legend={legend} setLegend={setLegend} />
    </div>
  );
}

interface SegmentPieChartLegendProps {
  isEnable: boolean;
  legend: SegmentsPieChartLegend;
  setLegend: Dispatch<SetStateAction<SegmentsPieChartLegend>>;
}

function SegmentPieChartLegend({ isEnable, legend, setLegend }: SegmentPieChartLegendProps) {
  const [isShowAll, setIsShowAll] = useState(false);

  const legendsToShow = isShowAll ? legend.nodes : legend.nodes.slice(0, 5);

  if (!isEnable) {
    return null;
  }

  return (
    <div className="z-20 -mt-36 flex flex-col items-start justify-center gap-2 px-8 pb-4 sm:-mt-16 lg:flex-row lg:flex-wrap lg:items-center lg:px-4">
      {legendsToShow.map((node, index) => {
        const hasSelected = legend.selectedNode !== null;
        const isSelectedNode = legend.selectedNode === node;

        return (
          <button
            key={node}
            type="button"
            className={classNames("flex w-fit items-center justify-center space-x-1", {
              "line-through": hasSelected && !isSelectedNode,
            })}
            onMouseEnter={() => setLegend((prev) => ({ ...prev, hoverNode: node }))}
            onMouseLeave={() => setLegend((prev) => ({ ...prev, hoverNode: null }))}
            onClick={() =>
              setLegend((prev) => {
                if (prev.selectedNode === node) {
                  return { ...prev, selectedNode: null, hoverNode: null };
                }

                return { ...prev, selectedNode: node, hoverNode: null };
              })
            }
          >
            <div
              className="h-2.5 w-2.5 rounded-full"
              style={{
                backgroundColor: getContourColors(index).background,
                borderColor: getContourColors(index).text,
              }}
            />
            <span className="max-w-52 overflow-hidden text-ellipsis whitespace-nowrap text-center font-aeonik text-xs font-medium text-slate-800 sm:max-w-60">
              {node}
            </span>
          </button>
        );
      })}

      <button
        type="button"
        className="font-aeonik text-xs font-medium text-brand hover:underline"
        onClick={() => setIsShowAll((prev) => !prev)}
      >
        {isShowAll ? (
          <FormattedMessage
            defaultMessage="Mostrar menos"
            description="Botão para retrair a legenda do gráfico de pizza de segmentos"
            id="uhn+YY"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Mostrar mais"
            description="Botão para expandir a legenda do gráfico de pizza de segmentos"
            id="hZMlEM"
          />
        )}
      </button>
    </div>
  );
}
